/**
 * @fileoverview Servicio de análisis semántico multilingüe para intenciones de consolidación de mensajes.
 * @module consolidationSemanticAnalyzer
 * @category Utility / Helper - Inbox
 */

import {
  DEFAULT_CONSOLIDATION_TIME_SECONDS,
  SHORT_PHRASE_MAX_CHAR_THRESHOLD,
  HUMAN_TYPING_PAUSE_THRESHOLD_MS,
  TERMINAL_PUNCTUATION_PAUSE_MS,
  CLOSING_PHRASE_PAUSE_MS
} from '../constants/consolidationConstants';

// Patrón IA Multilingüe 1: Saludos iniciales de apertura (Español, Inglés, Portugués)
const GREETING_REGEX = /^(hola|buenas|buen[oa]s?\s+(días|tardes|noches)|saludos|estimad[oa]s?|qué tal|que tal|hello|hi|hey|good\s+(morning|afternoon|evening)|greetings|dear|olá|oi|bom\s+dia|boa\s+(tarde|noite)|saudações|prezad[oa]s?)\b/i;

// Patrón IA Multilingüe 2: Conectores gramaticales abiertos al final de la frase (ES, EN, PT)
const OPEN_CONNECTOR_REGEX = /(y|o|pero|sino|porque|por qué|ya que|puesto que|es decir|por ejemplo|como|que|donde|cuando|así que|entonces|además|también|para|por|and|or|but|because|so|that|to|for|which|where|when|then|e|ou|mas|porém|por que|então)\s*$/i;

// Patrón IA Multilingüe 3: Puntuación terminal definitiva (. ! ? …)
const TERMINAL_PUNCTUATION_REGEX = /[.!?…]\s*$/;

// Patrón IA Multilingüe 4: Fórmulas de cierre / despedida explícitas (ES, EN, PT)
const CLOSING_PHRASE_REGEX = /(chao|hasta luego|que tengas|lindo día|buen día|feliz tarde|feliz noche|saludos|quedo atento|quedo a la orden|muchas gracias|mil gracias|cualquier duda|me avisas|un gusto|a la orden|bye|goodbye|have a (nice|great|good) day|have a good one|thank you|thanks|thanks a lot|best regards|regards|sincerely|let me know|happy to help|tchau|até logo|tenha um (bom|ótimo) dia|obrigado|obrigada|muito obrigado|saudações|cumprimentos|disposição|disposicao|qualquer dúvida|qualquer duvida|avise)\b/i;

/**
 * Analizador semántico de Inteligencia Artificial para consolidación de mensajes.
 * Evalúa completitud sintáctica, conectores abiertos, saludos, fórmulas de cierre y umbral adaptativo de tipeo.
 * Soporta patrones multilingües (Español, Inglés y Portugués).
 *
 * @param {string} text - Texto ingresado por el agente
 * @returns {Object} Diagnóstico semántico del mensaje
 */
export function analyzeSemanticIntent(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return {
      isGreeting: false,
      isOpenConnector: false,
      hasTerminalPunctuation: false,
      isClosingPhrase: false,
      isLongCompleteSentence: false,
      adaptivePauseMs: HUMAN_TYPING_PAUSE_THRESHOLD_MS
    };
  }

  const isGreeting = GREETING_REGEX.test(trimmed);
  const isOpenConnector = OPEN_CONNECTOR_REGEX.test(trimmed);
  const hasTerminalPunctuation = TERMINAL_PUNCTUATION_REGEX.test(trimmed);
  const isClosingPhrase = CLOSING_PHRASE_REGEX.test(trimmed);
  const isLongCompleteSentence = trimmed.length > SHORT_PHRASE_MAX_CHAR_THRESHOLD && !isOpenConnector && !isGreeting;

  let adaptivePauseMs = HUMAN_TYPING_PAUSE_THRESHOLD_MS;
  if (isGreeting && !isOpenConnector) {
    adaptivePauseMs = DEFAULT_CONSOLIDATION_TIME_SECONDS * 1000;
  } else if (isClosingPhrase) {
    adaptivePauseMs = CLOSING_PHRASE_PAUSE_MS;
  } else if (hasTerminalPunctuation) {
    adaptivePauseMs = TERMINAL_PUNCTUATION_PAUSE_MS;
  }

  return {
    isGreeting,
    isOpenConnector,
    hasTerminalPunctuation,
    isClosingPhrase,
    isLongCompleteSentence,
    adaptivePauseMs
  };
}
